<template>
    <div class="grid">
        <div class="unit whole">
            <h1>View Teachers</h1>
            <p class="lead">
                When pupils are in the correct classes, assign the teacher(s) to the class.
                <a href="/downloads/Changing-Classes-Guidelines.pdf" download="">See guidelines</a>
            </p>
            <a :class="{ disabled: selectedRows.length == 0 }" class="btn pull-right" @click="deactivateTeacherRows"
                >Deactivate selected</a
            >
            <RouterLink class="btn pull-right mx-5" to="/admin/alloc-teachers">Allocate teachers</RouterLink>
            <RouterLink class="btn pull-right" to="/admin/add-teacher">Add a new teacher</RouterLink>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="panel">
                <v-data-table
                    v-model="selectedRows"
                    show-select
                    density="compact"
                    items-per-page="20"
                    :headers="headers"
                    :items="items"
                    item-value="id"
                    :sort-by="sortBy"
                    class="elevation-0"
                >
                    <template #[`item.classes`]="{ value: item }">
                        {{ item.map((c: Class) => c.name).join(', ') }}</template
                    >
                    <template #[`item.last_active.timestamp`]="{ item }">{{ item.last_active.text }} </template>
                    <template #[`item.action`]="{ item }">
                        <div class="d-flex">
                            <RouterLink class="mr-3" :to="`/admin/teacher/${item.id}`"
                                ><v-icon icon="mdi-square-edit-outline"></v-icon
                            ></RouterLink>
                            <a @click="deactivateTeacher(item)"
                                ><v-icon color="error" icon="mdi-account-cancel"></v-icon
                            ></a>
                        </div>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.teachers" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { DateTime } from 'luxon';
import { VDataTable } from 'vuetify/components/VDataTable';
import { Class } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'teachersnav' } });

const { snackbarSuccess } = useSnackbar();
const store = useAdminStore();
store.fetchTeachers();
const { teachers, isLoaded } = storeToRefs(store);

const confirm = useConfirmDialog();
const { formatDate } = useDateFormat();

const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);
const selectedRows = ref<Array<any>>([]);

const items = computed(() => {
    return teachers.value.map((teacher) => {
        let lastActiveText;
        let timestamp = 0;
        if (teacher.last_active) {
            const lastActive = DateTime.fromISO(teacher.last_active);
            if (lastActive.isValid) {
                timestamp = lastActive.toMillis();
                const daysAgo = lastActive.diffNow().negate().as('days');
                if (daysAgo < 1) {
                    lastActiveText = 'Today';
                } else if (daysAgo < 2) {
                    lastActiveText = 'Yesterday';
                } else {
                    lastActiveText = formatDate(teacher.last_active);
                }
            }
        }
        return {
            ...teacher,
            last_active: { timestamp, text: lastActiveText },
        };
    });
});

async function deactivateTeacherRows() {
    const res = await confirm(
        'Deactivate teacher' + (selectedRows.value.length === 1 ? '' : 's'),
        selectedRows.value.length === 1
            ? `Are you sure you want to deactivate the selected teacher?`
            : `Are you sure you want to deactivate the ${selectedRows.value.length} selected teachers?`,
    );
    if (res) {
        await store.deactivateTeachers(selectedRows.value);
        selectedRows.value = [];
        await store.fetchTeachers();
        snackbarSuccess('Teacher details have been updated.');
    }
}

async function deactivateTeacher(item: any) {
    const res = await confirm('Deactivate teacher', `Are you sure you want to deactivate the teacher ${item.name}?`);
    if (res) {
        await store.deactivateTeachers([item.id]);
        selectedRows.value = [];
        await store.fetchTeachers();
        snackbarSuccess('Teacher details have been updated.');
    }
}

const headers = [
    {
        title: 'Teacher',
        key: 'name',
    },
    { title: 'Username', key: 'username' },
    { title: 'Email', key: 'email' },
    { title: 'Classes', key: 'classes' },
    {
        title: 'Last active',
        key: 'last_active.timestamp',
    },
    { title: '', key: 'action' },
];
</script>
<style scoped lang="scss">
:deep(th) {
    vertical-align: revert;
}
:deep(td) {
    text-align: revert;
    vertical-align: revert;
    border-top: revert;
}
.v-table .v-icon {
    font-size: 1.1rem;
}
</style>
